import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import * as Button from "components/Button";
import { DropdownSingleProps } from "../types";
import { DropdownSize, DropdownButtonType } from "../constants";
import { DropdownMatchesType } from "../constants";
import ChevronSVG from "modules/theme/icons/chevrons/right-chevron.svg";
import CrossSVG from "modules/theme/icons/general/cross.svg";
import LoadingSVG from "modules/theme/icons/loading/button-loading.svg";
import { useDropdownPosition } from "../hook";

import * as styles from "../styles.module.scss";

const Single: React.FC<DropdownSingleProps> = ({
  activeItems,
  appendTo,
  buttonType = DropdownButtonType.CHECKBOX,
  closeOnSelection,
  dropdownSelector = DropdownMatchesType.ID,
  handleToggleDropdown,
  forwardedButtonRef,
  forwardedContainerRef,
  forwardedDropdownRef,
  label,
  empty,
  items,
  name,
  setActiveItems,
  size = DropdownSize.LARGE,
  toggleDropdown,
  type,
}) => {
  const [filterRes, setFilterRes] = useState(items);
  const [keyWord, setKeyword] = useState("");
  const [isEmpty, setIsEmpty] = useState(empty ? true : false);
  const isCheckbox = buttonType === DropdownButtonType.CHECKBOX;
  const isLoading = items !== undefined && items.length === 0;
  const ref = useRef<HTMLButtonElement>(null);
  const width = useDropdownPosition(forwardedContainerRef);

  const className = clsx(
    styles.dropdown,
    toggleDropdown && styles.isOpen,
    styles[`${size}`],
    isLoading && styles.loading,
    isEmpty && styles.empty,
    activeItems.length > 1 && styles.multiSelectActive,
    activeItems.length === 1 && styles.singleSelectActive
  );

  const labelName = () => {
    if (activeItems.length > 1) {
      return activeItems.length.toString();
    } else if (activeItems.length === 1 && !activeItems.includes(label)) {
      const single = items?.filter((i) => i[dropdownSelector] === activeItems[0]);
      return single && single[0]?.title;
    }
    return name;
  };

  const filter = (e) => {
    const keyword = e.target.value;

    if (keyword !== "") {
      const results =
        items &&
        items.filter((item) => {
          return item.title.toLowerCase().startsWith(keyword.toLowerCase());
        });
      setFilterRes(results);
    } else {
      setFilterRes(items);
    }
    setKeyword(keyword);
  };

  const handleClear = () => {
    setFilterRes(() => items);
    setKeyword("");
    setActiveItems("", type);
  };

  useEffect(() => {
    if (items === undefined) {
      setIsEmpty(true);
    } else {
      setIsEmpty(false);
      setFilterRes(items);
    }
  }, [items]);

  useEffect(() => {
    ref && ref.current && closeOnSelection && handleToggleDropdown();
  }, [activeItems]);

  return (
    <div className={className}>
      <Button.Generic
        disabled={isLoading || isEmpty}
        label={labelName()}
        onClick={handleToggleDropdown}
        forwardedRef={forwardedButtonRef}
      >
        {isLoading ? <LoadingSVG /> : <ChevronSVG />}
      </Button.Generic>
      {toggleDropdown && (
        <div className={styles.dropdownList} style={{ width: size === DropdownSize.LARGE ? `${width}px` : "auto" }}>
          <div ref={forwardedDropdownRef}>
            {items && items.length > 25 && (
              <div className={styles.filterSelectContainer}>
                <input onInput={filter} placeholder="Search" type="text" value={keyWord} />
                <CrossSVG
                  onClick={() => {
                    setFilterRes(items);
                    setKeyword("");
                  }}
                />
              </div>
            )}
            <div>
              <ul>
                <li>
                  <Button.Generic
                    className={activeItems.length === 0 ? styles.isActive : ""}
                    label={label}
                    onClick={() => handleClear()}
                    forwardedRef={ref}
                  >
                    {activeItems.length === 0 && isCheckbox && <CrossSVG />}
                  </Button.Generic>
                </li>
                {filterRes &&
                  filterRes.map((item, key) => {
                    const id = () => {
                      switch (dropdownSelector) {
                        case DropdownMatchesType.TITLE:
                          return item.title ? item.title : "";
                        case DropdownMatchesType.SLUG:
                          return item.slug ? item.slug : "";
                        case DropdownMatchesType.ID:
                          return item.id ? item.id : "";
                        case DropdownMatchesType.UID:
                          return item.uid ? item.uid : "";
                        default:
                          return "";
                      }
                    };
                    const appendLabel = appendTo ? item.title + appendTo : item.title;
                    const buttonClassName = clsx(activeItems.includes(id()) && styles.isActive);
                    return (
                      <li key={id() + key}>
                        {isCheckbox ? (
                          <Button.Generic
                            className={buttonClassName}
                            label={appendLabel}
                            onClick={() => setActiveItems(id(), type)}
                          >
                            {activeItems.includes(id()) && isCheckbox && <CrossSVG />}
                          </Button.Generic>
                        ) : (
                          <Button.Generic
                            className={buttonClassName}
                            label={appendLabel}
                            onClick={() => {
                              setActiveItems("", type);
                              setActiveItems(id(), type);
                            }}
                          >
                            {activeItems.includes(id()) && isCheckbox && <CrossSVG />}
                          </Button.Generic>
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Single;
